import Dexie, {} from 'dexie';
import { canonicalJsonStringify, compareInstants, decimalCursorSchema, deviceCursorSchema, indexedDbMigrationExecutionSchema, indexedDbMigrationPolicySchema, instantSchema, jsonValueSchema, localProfileSchema, migrationPath, pendingSyncOperationSchema, sha256HexUtf8, tripRevisionSchema, uuidSchema, } from '@der-maschinfuehrer/contracts';
import { entityRowFromPayload, parseEntityRowEnvelope, parseLegacyShiftAggregateEnvelope, parsePendingOperation, } from './entity.js';
import { ENTITY_STORE, LEGACY_V1_STORES, LOCAL_DB_SCHEMA_VERSION, ProfileDatabase, } from './database.js';
class VersionTwoMigrationDatabase extends Dexie {
    entities;
    outbox;
    drafts;
    profiles;
    runtime_state;
    device_cursors;
    trip_revisions;
    migration_evidence;
    constructor(databaseName, dependencies) {
        super(databaseName, {
            autoOpen: false,
            cache: 'cloned',
            ...(dependencies === undefined
                ? {}
                : { indexedDB: dependencies.indexedDB, IDBKeyRange: dependencies.IDBKeyRange }),
        });
        this.version(1).stores(LEGACY_V1_STORES);
        this.version(2).stores({ entities: ENTITY_STORE });
    }
}
const MIGRATION_IMPLEMENTATION_VERSION = '1.0.0';
const PROFILE_SCHEMA_SHA256_BY_VERSION = new Map([
    [1, '2fb450052b2e11c4aed8f043dd96ed91ec9666061994bd58ae13247c0ce5375e'],
    [2, '6a8bf8803af9daea82bff2269f9f4c541469d3cbce52644dbbd73ff27cf4e5fb'],
    [3, '8fd4ff6be5392aa8e45b67d4a319ed15c5e4d61026ca8e08e8225f9ca6c32707'],
]);
const MIGRATION_POLICY = indexedDbMigrationPolicySchema.parse({
    contract_version: 'indexeddb_migration_policy.v1',
    policy_version: '1.0.0',
    generated_at: '2026-08-01T00:00:00.000Z',
    minimum_supported_schema_version: 1,
    current_schema_version: LOCAL_DB_SCHEMA_VERSION,
    preflight_on_database_copy: true,
    atomic_activation: true,
    preserve_previous_until_validation: true,
    migrations: [1, 2].map((fromSchemaVersion) => ({
        migration_id: `indexeddb-${String(fromSchemaVersion)}-to-${String(fromSchemaVersion + 1)}`,
        from_schema_version: fromSchemaVersion,
        to_schema_version: fromSchemaVersion + 1,
        implementation_version: MIGRATION_IMPLEMENTATION_VERSION,
        strategy: 'copy_transform_validate_atomic_swap',
        source_database_access: 'read_only',
        target_database_kind: 'staging_copy',
        on_failure: 'keep_previous_database',
        rollback_supported: true,
        fixture_sha256: [
            sha256HexUtf8(`dmf.indexeddb-migration-fixture.v1\u0000${String(fromSchemaVersion)}`),
        ],
        expected_schema_sha256: requiredProfileSchemaSha256(fromSchemaVersion + 1),
    })),
});
export async function copyAndMigrateProfileDatabase(sourceDatabaseName, sourceSchemaVersion, targetDatabaseNames, migrationOwner, dependencies, runtime) {
    const path = migrationPath(MIGRATION_POLICY, sourceSchemaVersion);
    if (path === null || path.length === 0) {
        throw new Error('no complete contiguous migration path exists for the source schema');
    }
    if (targetDatabaseNames.length !== path.length) {
        throw new Error('migration targets do not cover the complete contiguous policy path');
    }
    let stepSourceName = sourceDatabaseName;
    let stepSourceVersion = sourceSchemaVersion;
    const evidence = [];
    let finalDatabase = null;
    for (const [index, step] of path.entries()) {
        const targetDatabaseName = targetDatabaseNames[index];
        if (targetDatabaseName === undefined) {
            throw new Error('migration target database name is missing');
        }
        const result = await copyAndValidateMigrationStep(stepSourceName, stepSourceVersion, targetDatabaseName, step, migrationOwner, dependencies, runtime);
        evidence.push(result.evidence);
        if (step.to_schema_version === LOCAL_DB_SCHEMA_VERSION) {
            finalDatabase = result.database;
            try {
                runtime.fault?.('after_migration_step');
            }
            catch (error) {
                finalDatabase.close();
                throw error;
            }
        }
        else {
            result.database.close();
            runtime.fault?.('after_migration_step');
        }
        stepSourceName = targetDatabaseName;
        stepSourceVersion = step.to_schema_version;
    }
    if (finalDatabase === null) {
        throw new Error('migration path did not produce the current schema');
    }
    try {
        await validateMigrationEvidenceChain(finalDatabase, sourceSchemaVersion, evidence);
        indexedDbMigrationExecutionSchema.parse({
            policy_version: MIGRATION_POLICY.policy_version,
            source_schema_version: sourceSchemaVersion,
            target_schema_version: LOCAL_DB_SCHEMA_VERSION,
            source_database_name: sourceDatabaseName,
            staging_database_name: finalDatabase.name,
            source_checkpoint_sha256: evidence[0]?.source_checkpoint_sha256,
            started_at: runtime.now(),
            validation_status: 'passed',
            activation_status: 'activated',
        });
        return { database: finalDatabase, evidence };
    }
    catch (error) {
        finalDatabase.close();
        throw error;
    }
}
async function copyAndValidateMigrationStep(sourceDatabaseName, sourceSchemaVersion, targetDatabaseName, step, migrationOwner, dependencies, runtime) {
    if (step.from_schema_version !== sourceSchemaVersion ||
        step.to_schema_version !== sourceSchemaVersion + 1) {
        throw new Error('migration step does not match its physical source schema');
    }
    const snapshot = await readSourceSnapshot(sourceDatabaseName, sourceSchemaVersion, dependencies);
    const target = createMigrationTarget(targetDatabaseName, step.to_schema_version, dependencies);
    try {
        await target.open();
        assertPhysicalSchema(target, step.to_schema_version, 'migration target');
        const actualTargetSchemaSha256 = profileSchemaSha256(target, step.to_schema_version);
        if (actualTargetSchemaSha256 !== step.expected_schema_sha256) {
            throw new Error(`migration policy schema hash mismatch: expected ${step.expected_schema_sha256}, actual ${actualTargetSchemaSha256}`);
        }
        await populateMigrationTarget(target, snapshot, step.to_schema_version, targetDatabaseName, migrationOwner);
        const targetRecordCount = await validateMigratedDatabase(target, step.to_schema_version, step.to_schema_version === LOCAL_DB_SCHEMA_VERSION ? 'open' : 'migration');
        if (targetRecordCount !== snapshot.source_record_count) {
            throw new Error(`migration record count mismatch: source ${String(snapshot.source_record_count)}, target ${String(targetRecordCount)}`);
        }
        const targetCheckpointSha256 = await profileDatabaseCheckpointSha256(target, step.to_schema_version);
        const evidence = {
            migration_id: step.migration_id,
            policy_version: MIGRATION_POLICY.policy_version,
            implementation_version: step.implementation_version,
            from_schema_version: step.from_schema_version,
            to_schema_version: step.to_schema_version,
            source_database_name: sourceDatabaseName,
            target_database_name: targetDatabaseName,
            source_schema_sha256: requiredProfileSchemaSha256(sourceSchemaVersion),
            target_schema_sha256: actualTargetSchemaSha256,
            source_checkpoint_sha256: snapshot.source_checkpoint_sha256,
            target_checkpoint_sha256: targetCheckpointSha256,
            source_record_count: snapshot.source_record_count,
            target_record_count: targetRecordCount,
            migrated_at: runtime.now(),
            validation_status: 'passed',
        };
        await target.migration_evidence.put(evidence);
        return { database: target, evidence };
    }
    catch (error) {
        target.close();
        await target.delete();
        throw error;
    }
}
function createMigrationTarget(databaseName, schemaVersion, dependencies) {
    if (schemaVersion === 2) {
        return new VersionTwoMigrationDatabase(databaseName, dependencies);
    }
    if (schemaVersion === LOCAL_DB_SCHEMA_VERSION) {
        return new ProfileDatabase(databaseName, dependencies);
    }
    throw new Error(`unsupported migration target schema ${String(schemaVersion)}`);
}
async function populateMigrationTarget(target, snapshot, targetSchemaVersion, targetDatabaseName, migrationOwner) {
    const isFinalTarget = targetSchemaVersion === LOCAL_DB_SCHEMA_VERSION;
    const profiles = snapshot.profiles.map((row) => ({
        ...row,
        value: localProfileSchema.parse({
            ...row.value,
            indexeddb_schema_version: targetSchemaVersion,
        }),
    }));
    const outbox = snapshot.outbox.map((row) => parsePendingOperation(pendingSyncOperationSchema.parse(row)));
    const drafts = snapshot.drafts.map((row) => parseDraftRecord(row));
    const migratedProfile = profiles[0]?.value;
    const runtimeState = snapshot.runtime_state.map((row) => {
        const hasExplicitMutationGeneration = row.mutation_generation !== undefined;
        const mutationGeneration = decimalCursorSchema.parse(row.mutation_generation ?? (migratedProfile?.last_mutation_at === null ? '0' : '1'));
        return {
            ...row,
            write_fence: isFinalTarget ? 'open' : 'migration',
            migration_fence_owner: isFinalTarget ? null : migrationOwner,
            migration_fence_source_generation: isFinalTarget ? null : mutationGeneration,
            physical_database_name: targetDatabaseName,
            mutation_generation: mutationGeneration,
            protected_mutation_generation: hasExplicitMutationGeneration && row.protected_mutation_generation !== undefined
                ? row.protected_mutation_generation
                : null,
        };
    });
    const deviceCursors = snapshot.device_cursors.map((row) => deviceCursorSchema.parse(row));
    const tripRevisions = snapshot.trip_revisions.map((row) => tripRevisionSchema.parse(row));
    await target.transaction('rw', [
        target.entities,
        target.outbox,
        target.drafts,
        target.profiles,
        target.runtime_state,
        target.device_cursors,
        target.trip_revisions,
        target.migration_evidence,
    ], async () => {
        await target.entities.bulkPut(snapshot.entities);
        await target.outbox.bulkPut(outbox);
        await target.drafts.bulkPut(drafts);
        await target.profiles.bulkPut(profiles);
        await target.runtime_state.bulkPut(runtimeState);
        await target.device_cursors.bulkPut(deviceCursors);
        await target.trip_revisions.bulkPut(tripRevisions);
        await target.migration_evidence.bulkPut(snapshot.migration_evidence);
    });
}
async function readSourceSnapshot(databaseName, expectedSchemaVersion, dependencies) {
    const source = dynamicProfileDatabase(databaseName, dependencies);
    try {
        await source.open();
        assertPhysicalSchema(source, expectedSchemaVersion, 'migration source');
        return await source.transaction('r', source.tables, async () => {
            const entitiesTable = findTable(source, 'entities');
            const legacyTable = findTable(source, 'shift_aggregates');
            const legacyEntities = ((await legacyTable?.toArray()) ?? []).map((row) => {
                const legacy = parseLegacyShiftAggregateEnvelope(row);
                return entityRowFromPayload({ entity_type: 'shift_aggregate', value: legacy.aggregate }, legacy.dataset_epoch);
            });
            const entities = entitiesTable === undefined
                ? legacyEntities
                : (await entitiesTable.toArray()).map((row) => parseEntityRowEnvelope(row));
            if (entitiesTable !== undefined && legacyEntities.length > 0) {
                const entitiesByKey = new Map(entities.map((row) => [`${row.entity_type}\u0000${row.entity_id}`, row]));
                for (const legacyEntity of legacyEntities) {
                    const migratedEntity = entitiesByKey.get(`${legacyEntity.entity_type}\u0000${legacyEntity.entity_id}`);
                    if (migratedEntity === undefined ||
                        canonicalJsonStringify(migratedEntity) !==
                            canonicalJsonStringify(legacyEntity)) {
                        throw new Error('version-two legacy aggregate differs from its entity projection');
                    }
                }
            }
            const outbox = (await readTable(source, 'outbox')).map((row) => parsePendingOperation(pendingSyncOperationSchema.parse(row)));
            const drafts = (await readTable(source, 'drafts')).map((row) => parseDraftRecord(row));
            const profiles = (await readTable(source, 'profiles')).map((row) => {
                if (typeof row !== 'object' || row === null || row.key !== 'profile') {
                    throw new Error('migration source profile row is malformed');
                }
                return {
                    key: 'profile',
                    value: localProfileSchema.parse(row.value),
                };
            });
            const runtimeState = (await readTable(source, 'runtime_state'));
            const deviceCursors = (await readTable(source, 'device_cursors')).map((row) => deviceCursorSchema.parse(row));
            const tripRevisions = (await readTable(source, 'trip_revisions')).map((row) => tripRevisionSchema.parse(row));
            const migrationEvidence = (await readTable(source, 'migration_evidence'));
            validateSourceSnapshotRows(databaseName, expectedSchemaVersion, entities, outbox, drafts, profiles, runtimeState, deviceCursors, tripRevisions);
            const sourceCheckpointSha256 = databaseSnapshotSha256(expectedSchemaVersion, source.tables.map((table) => ({
                name: table.name,
                schema: table.schema.primKey.src,
                indexes: table.schema.indexes.map((index) => index.src).sort(),
            })), {
                entities,
                outbox,
                drafts,
                profiles,
                runtime_state: runtimeState,
                device_cursors: deviceCursors,
                trip_revisions: tripRevisions,
            });
            return {
                entities,
                outbox,
                drafts,
                profiles,
                runtime_state: runtimeState,
                device_cursors: deviceCursors,
                trip_revisions: tripRevisions,
                migration_evidence: migrationEvidence,
                source_checkpoint_sha256: sourceCheckpointSha256,
                source_record_count: entities.length +
                    outbox.length +
                    drafts.length +
                    profiles.length +
                    runtimeState.length +
                    deviceCursors.length +
                    tripRevisions.length,
            };
        });
    }
    finally {
        source.close();
    }
}
function validateSourceSnapshotRows(databaseName, expectedSchemaVersion, entities, outbox, drafts, profiles, runtimeRows, deviceCursors, tripRevisions) {
    if (profiles.length !== 1 || runtimeRows.length !== 1) {
        throw new Error('migration source must contain exactly one profile and runtime state');
    }
    const profile = localProfileSchema.parse(profiles[0]?.value);
    const runtime = runtimeRows[0];
    if (runtime === undefined ||
        profile.indexeddb_schema_version !== expectedSchemaVersion ||
        runtime.local_profile_id !== profile.local_profile_id ||
        runtime.dataset_epoch !== profile.dataset_epoch ||
        (runtime.physical_database_name !== undefined &&
            runtime.physical_database_name !== databaseName)) {
        throw new Error('migration source runtime state does not match its profile or database');
    }
    const entitiesByIdentity = new Map(entities.map((row) => {
        const parsed = parseEntityRowEnvelope(row, profile.dataset_epoch);
        return [`${parsed.entity_type}\u0000${parsed.entity_id}`, parsed];
    }));
    for (const operation of outbox) {
        if (operation.local_profile_id !== profile.local_profile_id ||
            operation.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migration source outbox failed profile or epoch validation');
        }
        const entity = entitiesByIdentity.get(`${operation.payload.entity_type}\u0000${operation.entity_id}`);
        if (entity === undefined || entity.revision < operation.resulting_revision) {
            throw new Error('migration source outbox has no corresponding domain revision');
        }
    }
    for (const draft of drafts) {
        if (draft.local_profile_id !== profile.local_profile_id ||
            draft.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migration source draft failed profile or epoch validation');
        }
    }
    for (const cursorInput of deviceCursors) {
        const cursor = deviceCursorSchema.parse(cursorInput);
        if (profile.account_identity === null ||
            cursor.account_identity.issuer !== profile.account_identity.issuer ||
            cursor.account_identity.subject !== profile.account_identity.subject ||
            cursor.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migration source device cursor failed account or epoch validation');
        }
    }
    for (const revisionInput of tripRevisions) {
        if (tripRevisionSchema.parse(revisionInput).dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migration source trip revision failed epoch validation');
        }
    }
}
export async function validateProfileDatabaseMigrationSource(databaseName, expectedSchemaVersion, dependencies) {
    await readSourceSnapshot(databaseName, expectedSchemaVersion, dependencies);
}
export async function readProfileWithoutUpgrade(databaseName, expectedSchemaVersion, dependencies) {
    const source = dynamicProfileDatabase(databaseName, dependencies);
    try {
        await source.open();
        assertPhysicalSchema(source, expectedSchemaVersion, 'profile discovery');
        const profiles = findTable(source, 'profiles');
        const runtimeState = findTable(source, 'runtime_state');
        if (profiles === undefined || runtimeState === undefined) {
            throw new Error('profile discovery source is missing required control tables');
        }
        return await source.transaction('r', [profiles, runtimeState], async () => {
            const [profileRow, runtimeRow] = await Promise.all([
                profiles.get('profile'),
                runtimeState.get('runtime'),
            ]);
            if (profileRow === undefined || runtimeRow === undefined) {
                throw new Error('profile discovery source is missing required control rows');
            }
            return {
                profile: localProfileSchema.parse(profileRow.value),
                runtime_state: runtimeRow,
            };
        });
    }
    finally {
        source.close();
    }
}
async function profileDatabaseCheckpointSha256(database, schemaVersion) {
    return database.transaction('r', database.tables, async () => databaseSnapshotSha256(schemaVersion, database.tables.map((table) => ({
        name: table.name,
        schema: table.schema.primKey.src,
        indexes: table.schema.indexes.map((index) => index.src).sort(),
    })), {
        entities: await database.entities.toArray(),
        outbox: await database.outbox.toArray(),
        drafts: await database.drafts.toArray(),
        profiles: await database.profiles.toArray(),
        runtime_state: await database.runtime_state.toArray(),
        device_cursors: await database.device_cursors.toArray(),
        trip_revisions: await database.trip_revisions.toArray(),
    }));
}
function databaseSnapshotSha256(schemaVersion, schema, tables) {
    const canonicalTables = Object.entries(tables)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([name, rows]) => ({
        name,
        rows: [...rows].sort((left, right) => canonicalJsonStringify(left).localeCompare(canonicalJsonStringify(right))),
    }));
    const canonicalSchema = [...schema]
        .map((store) => ({ ...store, indexes: [...store.indexes].sort() }))
        .sort((left, right) => left.name.localeCompare(right.name));
    return sha256HexUtf8(`dmf.indexeddb-source-checkpoint.sha256.v1\u0000${canonicalJsonStringify({
        schema_version: schemaVersion,
        schema: canonicalSchema,
        tables: canonicalTables,
    })}`);
}
function requiredProfileSchemaSha256(schemaVersion) {
    const expected = PROFILE_SCHEMA_SHA256_BY_VERSION.get(schemaVersion);
    if (expected === undefined) {
        throw new Error(`profile schema ${String(schemaVersion)} has no pinned SHA-256`);
    }
    return expected;
}
function profileSchemaSha256(database, schemaVersion) {
    const stores = database.tables
        .map((table) => ({
        name: table.name,
        primary_key: table.schema.primKey.src,
        indexes: table.schema.indexes.map((index) => index.src).sort(),
    }))
        .sort((left, right) => left.name.localeCompare(right.name));
    return sha256HexUtf8(`dmf.indexeddb-schema.sha256.v1\u0000${canonicalJsonStringify({
        schema_version: schemaVersion,
        stores,
    })}`);
}
async function validateMigrationEvidenceChain(database, sourceSchemaVersion, expectedEvidence) {
    const expectedPath = migrationPath(MIGRATION_POLICY, sourceSchemaVersion);
    if (expectedPath === null || expectedPath.length !== expectedEvidence.length) {
        throw new Error('migration evidence does not cover a complete contiguous policy path');
    }
    let previousTarget = null;
    let previousTargetDatabase = null;
    for (const [index, step] of expectedPath.entries()) {
        const evidence = expectedEvidence[index];
        if (evidence === undefined ||
            evidence.migration_id !== step.migration_id ||
            evidence.from_schema_version !== step.from_schema_version ||
            evidence.to_schema_version !== step.to_schema_version ||
            evidence.to_schema_version !== evidence.from_schema_version + 1 ||
            evidence.policy_version !== MIGRATION_POLICY.policy_version ||
            evidence.source_schema_sha256 !== requiredProfileSchemaSha256(step.from_schema_version) ||
            evidence.target_schema_sha256 !== step.expected_schema_sha256 ||
            (previousTarget !== null && evidence.source_checkpoint_sha256 !== previousTarget) ||
            (previousTargetDatabase !== null && evidence.source_database_name !== previousTargetDatabase)) {
            throw new Error('migration evidence chain is missing, skipped, or reordered');
        }
        const stored = await database.migration_evidence.get(evidence.migration_id);
        if (stored === undefined ||
            canonicalJsonStringify(stored) !==
                canonicalJsonStringify(evidence)) {
            throw new Error('persisted migration evidence differs from validated execution evidence');
        }
        previousTarget = evidence.target_checkpoint_sha256;
        previousTargetDatabase = evidence.target_database_name;
    }
}
async function validateMigratedDatabase(database, expectedSchemaVersion, expectedWriteFence) {
    const profiles = await database.profiles.toArray();
    const runtimeRows = await database.runtime_state.toArray();
    if (profiles.length !== 1 || runtimeRows.length !== 1) {
        throw new Error('migrated database must contain exactly one profile and runtime state');
    }
    const profile = localProfileSchema.parse(profiles[0]?.value);
    const runtime = runtimeRows[0];
    if (runtime === undefined ||
        runtime.local_profile_id !== profile.local_profile_id ||
        runtime.dataset_epoch !== profile.dataset_epoch ||
        profile.indexeddb_schema_version !== expectedSchemaVersion ||
        runtime.write_fence !== expectedWriteFence ||
        runtime.physical_database_name !== database.name ||
        runtime.mutation_generation === undefined ||
        runtime.protected_mutation_generation === undefined) {
        throw new Error('migrated runtime state does not match its profile');
    }
    if (expectedWriteFence === 'migration' &&
        (runtime.migration_fence_owner === undefined ||
            runtime.migration_fence_owner === null ||
            runtime.migration_fence_source_generation !== runtime.mutation_generation)) {
        throw new Error('intermediate migration target has no owned write fence');
    }
    if (expectedWriteFence === 'open' &&
        (runtime.migration_fence_owner !== null || runtime.migration_fence_source_generation !== null)) {
        throw new Error('final migration target retained an intermediate write fence');
    }
    decimalCursorSchema.parse(runtime.mutation_generation);
    if (runtime.protected_mutation_generation !== null) {
        decimalCursorSchema.parse(runtime.protected_mutation_generation);
    }
    const entities = await database.entities.toArray();
    const entitiesByIdentity = new Map(entities.map((row) => [`${row.entity_type}\u0000${row.entity_id}`, row]));
    for (const row of entities) {
        parseEntityRowEnvelope(row, profile.dataset_epoch);
    }
    for (const operation of await database.outbox.toArray()) {
        const parsed = pendingSyncOperationSchema.parse(operation);
        if (parsed.local_profile_id !== profile.local_profile_id ||
            parsed.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migrated outbox operation failed profile or epoch validation');
        }
        const entity = entitiesByIdentity.get(`${parsed.payload.entity_type}\u0000${parsed.entity_id}`);
        if (entity === undefined || entity.revision < parsed.resulting_revision) {
            throw new Error('migrated outbox operation has no corresponding domain revision');
        }
        if (BigInt(parsed.local_sequence) >= BigInt(runtime.next_local_sequence)) {
            throw new Error('migrated runtime sequence does not follow every pending operation');
        }
    }
    const drafts = await database.drafts.toArray();
    for (const draftInput of drafts) {
        const draft = parseDraftRecord(draftInput);
        if (draft.local_profile_id !== profile.local_profile_id ||
            draft.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migrated draft failed profile or epoch validation');
        }
    }
    const deviceCursors = await database.device_cursors.toArray();
    for (const cursorInput of deviceCursors) {
        const cursor = deviceCursorSchema.parse(cursorInput);
        if (profile.account_identity === null ||
            cursor.account_identity.issuer !== profile.account_identity.issuer ||
            cursor.account_identity.subject !== profile.account_identity.subject ||
            cursor.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migrated device cursor failed account or epoch validation');
        }
    }
    const tripRevisions = await database.trip_revisions.toArray();
    for (const revisionInput of tripRevisions) {
        const revision = tripRevisionSchema.parse(revisionInput);
        if (revision.dataset_epoch !== profile.dataset_epoch) {
            throw new Error('migrated trip revision failed epoch validation');
        }
    }
    return (entities.length +
        (await database.outbox.count()) +
        drafts.length +
        profiles.length +
        runtimeRows.length +
        deviceCursors.length +
        tripRevisions.length);
}
export async function fenceProfileDatabaseForMigration(databaseName, migrationOwner, dependencies) {
    assertMigrationOwner(migrationOwner);
    const source = dynamicProfileDatabase(databaseName, dependencies);
    try {
        await source.open();
        const runtimeTable = findTable(source, 'runtime_state');
        const profileTable = findTable(source, 'profiles');
        if (runtimeTable === undefined || profileTable === undefined) {
            throw new Error('migration source has no runtime state or profile table');
        }
        return await source.transaction('rw', [runtimeTable, profileTable], async () => {
            const runtime = await runtimeTable.get('runtime');
            const profileRow = await profileTable.get('profile');
            if (runtime === undefined || profileRow === undefined) {
                throw new Error('migration source has no runtime state or profile row');
            }
            if (runtime.physical_database_name !== undefined &&
                runtime.physical_database_name !== databaseName) {
                throw new Error('migration source runtime state belongs to another physical database');
            }
            if (runtime.write_fence === 'migration') {
                if (runtime.migration_fence_owner !== migrationOwner ||
                    runtime.migration_fence_source_generation === undefined ||
                    runtime.migration_fence_source_generation === null) {
                    throw new Error('migration source is fenced by another owner');
                }
                return decimalCursorSchema.parse(runtime.migration_fence_source_generation);
            }
            if (runtime.write_fence !== undefined && runtime.write_fence !== 'open') {
                throw new Error('migration source is already protected by another write fence');
            }
            const profile = localProfileSchema.parse(profileRow.value);
            const sourceGeneration = decimalCursorSchema.parse(runtime.mutation_generation ?? (profile.last_mutation_at === null ? '0' : '1'));
            await runtimeTable.put({
                ...runtime,
                mutation_generation: sourceGeneration,
                write_fence: 'migration',
                migration_fence_owner: migrationOwner,
                migration_fence_source_generation: sourceGeneration,
            });
            return sourceGeneration;
        });
    }
    finally {
        source.close();
    }
}
export class WipeMutationGenerationChangedError extends Error {
    constructor() {
        super('local data changed before the account-switch wipe write fence was applied');
        this.name = 'WipeMutationGenerationChangedError';
    }
}
export async function fenceProfileDatabaseForWipe(databaseName, expectedMutationGeneration, dependencies) {
    const source = new Dexie(databaseName, {
        autoOpen: false,
        allowEmptyDB: false,
        ...(dependencies === undefined
            ? {}
            : { indexedDB: dependencies.indexedDB, IDBKeyRange: dependencies.IDBKeyRange }),
    });
    try {
        await source.open();
        const runtimeTable = findTable(source, 'runtime_state');
        if (runtimeTable === undefined) {
            throw new Error('wipe source has no runtime state table');
        }
        await source.transaction('rw', runtimeTable, async () => {
            const runtime = await runtimeTable.get('runtime');
            if (runtime === undefined) {
                throw new Error('wipe source has no runtime state row');
            }
            if (runtime.mutation_generation !== expectedMutationGeneration) {
                throw new WipeMutationGenerationChangedError();
            }
            if (runtime.physical_database_name !== databaseName) {
                throw new Error('wipe source runtime state belongs to another physical database');
            }
            if (runtime.write_fence === 'wipe') {
                return;
            }
            if (runtime.write_fence !== 'open') {
                throw new Error('wipe source is already protected by another write fence');
            }
            await runtimeTable.put({
                ...runtime,
                write_fence: 'wipe',
                migration_fence_owner: null,
                migration_fence_source_generation: null,
            });
        });
    }
    finally {
        source.close();
    }
}
export async function unfenceProfileDatabaseAfterFailedMigration(databaseName, migrationOwner, expectedSourceGeneration, dependencies) {
    assertMigrationOwner(migrationOwner);
    const expectedGeneration = decimalCursorSchema.parse(expectedSourceGeneration);
    const source = dynamicProfileDatabase(databaseName, dependencies);
    try {
        await source.open();
        const runtimeTable = findTable(source, 'runtime_state');
        if (runtimeTable === undefined) {
            throw new Error('failed migration source has no runtime state table');
        }
        await source.transaction('rw', runtimeTable, async () => {
            const runtime = await runtimeTable.get('runtime');
            if (runtime === undefined) {
                throw new Error('failed migration source has no runtime state row');
            }
            if (runtime.write_fence !== 'migration' ||
                runtime.migration_fence_owner !== migrationOwner ||
                runtime.migration_fence_source_generation !== expectedGeneration ||
                runtime.mutation_generation !== expectedGeneration) {
                throw new Error('failed migration source write fence is not owned by this migration');
            }
            await runtimeTable.put({
                ...runtime,
                write_fence: 'open',
                migration_fence_owner: null,
                migration_fence_source_generation: null,
                physical_database_name: databaseName,
            });
        });
    }
    finally {
        source.close();
    }
}
export async function assertProfileDatabaseMigrationFence(databaseName, migrationOwner, expectedSourceGeneration, dependencies) {
    assertMigrationOwner(migrationOwner);
    const expectedGeneration = decimalCursorSchema.parse(expectedSourceGeneration);
    const source = dynamicProfileDatabase(databaseName, dependencies);
    try {
        await source.open();
        const runtimeTable = findTable(source, 'runtime_state');
        const runtime = await runtimeTable?.get('runtime');
        if (runtime === undefined ||
            runtime.write_fence !== 'migration' ||
            runtime.migration_fence_owner !== migrationOwner ||
            runtime.migration_fence_source_generation !== expectedGeneration ||
            runtime.mutation_generation !== expectedGeneration) {
            throw new Error('migration source write fence ownership changed before activation');
        }
    }
    finally {
        source.close();
    }
}
function parseDraftRecord(input) {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw new Error('migrated draft must be an object');
    }
    const row = input;
    const allowedKeys = new Set([
        'draft_id',
        'draft_key',
        'draft_kind',
        'payload',
        'local_profile_id',
        'dataset_epoch',
        'autosave_revision',
        'created_at',
        'updated_at',
        'expanded_payload',
    ]);
    if (Object.keys(row).some((key) => !allowedKeys.has(key))) {
        throw new Error('migrated draft contains unsupported fields');
    }
    if (typeof row.draft_key !== 'string' ||
        row.draft_key.length === 0 ||
        row.draft_key.length > 256 ||
        row.draft_key.trim() !== row.draft_key) {
        throw new Error('migrated draft_key is invalid');
    }
    if (row.draft_kind !== 'shift_editor') {
        throw new Error('migrated draft_kind is unsupported');
    }
    if (typeof row.autosave_revision !== 'number' ||
        !Number.isSafeInteger(row.autosave_revision) ||
        row.autosave_revision < 1) {
        throw new Error('migrated draft autosave_revision must be a positive safe integer');
    }
    const createdAt = instantSchema.parse(row.created_at);
    const updatedAt = instantSchema.parse(row.updated_at);
    if (compareInstants(updatedAt, createdAt) < 0) {
        throw new Error('migrated draft updated_at precedes created_at');
    }
    return {
        draft_id: uuidSchema.parse(row.draft_id),
        draft_key: row.draft_key,
        draft_kind: row.draft_kind,
        payload: jsonValueSchema.parse(row.payload),
        local_profile_id: uuidSchema.parse(row.local_profile_id),
        dataset_epoch: uuidSchema.parse(row.dataset_epoch),
        autosave_revision: row.autosave_revision,
        created_at: createdAt,
        updated_at: updatedAt,
        ...(Object.hasOwn(row, 'expanded_payload')
            ? { expanded_payload: jsonValueSchema.parse(row.expanded_payload) }
            : {}),
    };
}
function dynamicProfileDatabase(databaseName, dependencies) {
    return new Dexie(databaseName, {
        autoOpen: false,
        allowEmptyDB: false,
        ...(dependencies === undefined
            ? {}
            : { indexedDB: dependencies.indexedDB, IDBKeyRange: dependencies.IDBKeyRange }),
    });
}
function assertPhysicalSchema(database, expectedSchemaVersion, context) {
    if (database.verno !== expectedSchemaVersion) {
        throw new Error(`${context} schema mismatch: directory ${String(expectedSchemaVersion)}, physical ${String(database.verno)}`);
    }
    const expectedSchemaSha256 = requiredProfileSchemaSha256(expectedSchemaVersion);
    const actualSchemaSha256 = profileSchemaSha256(database, expectedSchemaVersion);
    if (actualSchemaSha256 !== expectedSchemaSha256) {
        throw new Error(`${context} schema hash mismatch: expected ${expectedSchemaSha256}, actual ${actualSchemaSha256}`);
    }
}
function assertMigrationOwner(owner) {
    if (owner.length === 0 || owner.length > 256 || owner.trim() !== owner) {
        throw new Error('migration fence owner must be canonical non-empty text');
    }
}
function findTable(database, name) {
    return database.tables.find((table) => table.name === name);
}
async function readTable(database, name) {
    return (await findTable(database, name)?.toArray()) ?? [];
}
//# sourceMappingURL=migration.js.map